/**
 * Filter Preset Manager Component
 *
 * Provides UI for managing saved filter presets:
 * - Dropdown to select and apply saved presets
 * - "Salvar período" button to save current filters
 * - Delete button for each preset in dropdown
 *
 * Styled for Monutrack dark theme with Monumenta Green (#D2FA52).
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Save, Trash2, BookmarkPlus } from 'lucide-react';
import type { FilterPreset, FilterPresetFilters } from '../services/filterPresetService';

// ============================================================================
// TYPES
// ============================================================================

export interface FilterPresetManagerProps {
  /** List of saved presets */
  presets: FilterPreset[];
  /** Current filter state to save */
  currentFilters: FilterPresetFilters;
  /** Callback when a preset is selected */
  onApplyPreset: (preset: FilterPreset) => void;
  /** Callback when save button is clicked */
  onSaveClick: () => void;
  /** Callback when a preset is deleted */
  onDeletePreset: (presetId: string) => void;
  /** Whether controls are disabled */
  disabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FilterPresetManager({
  presets,
  currentFilters,
  onApplyPreset,
  onSaveClick,
  onDeletePreset,
  disabled = false,
}: FilterPresetManagerProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isDropdownOpen]);

  const handlePresetSelect = (preset: FilterPreset) => {
    onApplyPreset(preset);
    setIsDropdownOpen(false);
  };

  const handleDeleteClick = (e: React.MouseEvent, presetId: string) => {
    e.stopPropagation(); // Prevent triggering preset selection
    onDeletePreset(presetId);
  };

  const hasPresets = presets.length > 0;

  return (
    <div className="flex items-center gap-2">
      {/* Preset Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled || !hasPresets}
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
            transition-all duration-150 min-w-[180px] justify-between
            focus:outline-none focus:ring-2 focus:ring-[#D2FA52]
            ${
              disabled || !hasPresets
                ? 'bg-[#2A2D34] text-[#5A5D66] border border-[#3A3D44] cursor-not-allowed opacity-60'
                : 'bg-[#2A2D34] text-[#838998] border border-[#3A3D44] hover:bg-[#3A3D44] hover:text-[#D2FA52] cursor-pointer'
            }
          `}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
        >
          <span className="truncate">
            {hasPresets ? 'Selecionar preset' : 'Nenhum preset salvo'}
          </span>
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && hasPresets && (
          <div
            className="absolute z-50 mt-1 w-full min-w-[240px] bg-[#2A2D34] border border-[#3A3D44] rounded-lg shadow-lg overflow-hidden"
            role="listbox"
          >
            <ul className="max-h-[240px] overflow-y-auto py-1">
              {presets.map((preset) => (
                <li key={preset.id}>
                  <div
                    className="flex items-center justify-between px-4 py-2 hover:bg-[#3A3D44] cursor-pointer group transition-colors"
                    onClick={() => handlePresetSelect(preset)}
                    role="option"
                    aria-selected={false}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-white font-medium truncate block">
                        {preset.name}
                      </span>
                      <span className="text-xs text-[#838998]">
                        {preset.filters.startDate} → {preset.filters.endDate}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteClick(e, preset.id)}
                      className="ml-2 p-1 rounded hover:bg-red-500/20 text-[#838998] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Excluir preset ${preset.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Save Button */}
      <button
        type="button"
        onClick={onSaveClick}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
          transition-all duration-150
          focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-1 focus:ring-offset-[#24262B]
          ${
            disabled
              ? 'bg-[#3A3D44] text-[#838998] cursor-not-allowed'
              : 'bg-[#D2FA52] text-[#232429] hover:bg-[#c4eb44] cursor-pointer'
          }
        `}
        title="Salvar período atual como preset"
      >
        <BookmarkPlus className="w-4 h-4" />
        <span>Salvar período</span>
      </button>
    </div>
  );
}

export default FilterPresetManager;
